import React, { useState } from "react";
import {
  Box, Typography, TextField, Button, Alert, CircularProgress,
  InputAdornment, IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff, CheckCircleOutline } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import ThemeToggle from "./ThemeToggle";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const BRAND = { navy: "#0a0e27", navyDark: "#0f1233", accent: "#637cdb" };

function Field({ label, required, hint, isDark, children }) {
  const sub = isDark ? "rgba(255,255,255,0.55)" : "#475569";
  return (
    <Box sx={{ mb: 2.5 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.06em", color: sub, textTransform: "uppercase" }}>
          {label}{required && <Box component="span" sx={{ color: BRAND.accent, ml: 0.4 }}>*</Box>}
        </Typography>
        {hint && <Typography sx={{ fontSize: 12.5, color: sub }}>{hint}</Typography>}
      </Box>
      {children}
    </Box>
  );
}

export default function ResetPassword() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [newPassword, setNewPassword] = useState("");
  const [confirm,     setConfirm]     = useState("");
  const [showPw,      setShowPw]      = useState(false);
  const [loading,     setLoading]     = useState(false);
  const [success,     setSuccess]     = useState(false);
  const [message,     setMessage]     = useState(null);

  const bg      = isDark ? "#0a0f1e" : "#f0f4ff";
  const cardBg  = isDark ? "#111827" : "#ffffff";
  const textSub = isDark ? "#94a3b8" : "#64748b";

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      bgcolor: isDark ? "rgba(255,255,255,0.03)" : "rgba(10,14,39,0.018)",
      fontSize: 16,
      "& fieldset": { borderColor: isDark ? "rgba(255,255,255,0.09)" : "rgba(10,14,39,0.14)" },
      "&:hover fieldset": { borderColor: isDark ? "rgba(255,255,255,0.22)" : "rgba(10,14,39,0.3)" },
      "&.Mui-focused fieldset": { borderColor: BRAND.navy, borderWidth: "2px" },
      "&.Mui-focused": { boxShadow: "0 0 0 3px rgba(10,14,39,0.08)" },
    },
    "& .MuiOutlinedInput-input": { py: "13px", px: "14px" },
  };

  const pwAdornment = (
    <InputAdornment position="end">
      <IconButton onClick={() => setShowPw((p) => !p)} edge="end">
        {showPw ? <VisibilityOff /> : <Visibility />}
      </IconButton>
    </InputAdornment>
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);
    if (!token) return setMessage({ severity: "error", text: "Invalid or missing reset link." });
    if (newPassword.length < 8)
      return setMessage({ severity: "error", text: "Password must be at least 8 characters." });
    if (newPassword !== confirm)
      return setMessage({ severity: "error", text: "Passwords do not match." });

    setLoading(true);
    try {
      await axios.post(`${API_BASE}/reset-password`, { token, new_password: newPassword });
      setSuccess(true);
    } catch (err) {
      setLoading(false);
      setMessage({ severity: "error", text: err.response?.data?.detail || "Reset failed. The link may have expired." });
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: bg, display: "flex", flexDirection: "column",
      overflow: "hidden", position: "relative" }}>

      {/* Background blobs */}
      <Box sx={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 0 }}>
        {[
          { top: "-10%", left: "-5%",  size: 500, color: isDark ? "rgba(10,14,39,0.18)" : "rgba(10,14,39,0.07)", delay: 0 },
          { top: "60%",  right: "-8%", size: 400, color: isDark ? "rgba(99,124,219,0.08)" : "rgba(99,124,219,0.12)", delay: 1.5 },
        ].map((b, i) => (
          <motion.div key={i} style={{ position: "absolute", top: b.top, left: b.left, right: b.right,
            width: b.size, height: b.size, borderRadius: "50%", background: b.color, filter: "blur(80px)" }}
            animate={{ scale: [1, 1.12, 1], opacity: [0.8, 1, 0.8] }}
            transition={{ duration: 8 + i * 2, repeat: Infinity, delay: b.delay, ease: "easeInOut" }} />
        ))}
      </Box>

      {/* Header */}
      <motion.div initial={{ y: -64, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }} style={{ position: "relative", zIndex: 10 }}>
        <Box sx={{ bgcolor: isDark ? "rgba(2,6,15,0.88)" : "rgba(10,14,39,0.97)",
          backdropFilter: "blur(16px)", borderBottom: "2px solid rgba(99,124,219,0.3)" }}>
          <Box sx={{ maxWidth: 1200, mx: "auto", px: 3, height: 64,
            display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box component={Link} to="/"
              sx={{ display: "flex", alignItems: "center", textDecoration: "none" }}>
              <img src="/logo-light.png" alt="Proove" style={{ height: 20, width: "auto" }} />
            </Box>
            <ThemeToggle />
          </Box>
        </Box>
      </motion.div>

      {/* Main */}
      <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
        px: 3, py: 6, position: "relative", zIndex: 10 }}>
        <motion.div initial={{ y: 30, opacity: 0, scale: 0.97 }} animate={{ y: 0, opacity: 1, scale: 1 }}
          transition={{ duration: 0.55, delay: 0.15, ease: [0.34, 1.56, 0.64, 1] }}>

          <Box sx={{ width: { xs: "100%", sm: 460 }, maxWidth: 460, borderRadius: 3, overflow: "hidden",
            border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(10,14,39,0.1)"}`,
            boxShadow: isDark ? "0 24px 80px rgba(0,0,0,0.5)" : "0 24px 80px rgba(10,14,39,0.12)",
            bgcolor: cardBg }}>

            <Box sx={{ height: 4, background: `linear-gradient(to right, ${BRAND.navy}, ${BRAND.accent})` }} />

            {success ? (
              <>
                <Box sx={{ bgcolor: BRAND.navy, px: 4, pt: 3.5, pb: 3.5, textAlign: "center",
                  position: "relative", overflow: "hidden" }}>
                  <Box sx={{ position: "absolute", top: -40, right: -40, width: 140, height: 140,
                    borderRadius: "50%", border: "1px solid rgba(99,124,219,0.15)" }} />
                  <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}
                    transition={{ delay: 0.1, type: "spring", stiffness: 260, damping: 18 }}>
                    <CheckCircleOutline sx={{ fontSize: 56, color: "#22c55e", mb: 1.5 }} />
                  </motion.div>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.15em", color: BRAND.accent, mb: 0.75 }}>
                    PASSWORD RESET
                  </Typography>
                  <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#fff", lineHeight: 1.3 }}>
                    All done!
                  </Typography>
                </Box>
                <Box sx={{ px: 4, py: 3.5 }}>
                  <Typography sx={{ fontSize: 14.5, color: isDark ? "#cbd5e1" : "#475569",
                    lineHeight: 1.8, mb: 3, textAlign: "center" }}>
                    Your password has been reset. Sign in with your new password to continue.
                  </Typography>
                  <Button fullWidth variant="contained" component={Link} to="/"
                    sx={{
                      background: `linear-gradient(135deg, ${BRAND.navy} 0%, #3F6AC0 100%)`,
                      borderRadius: "10px", py: 1.6, fontWeight: 700, textTransform: "none", fontSize: 17,
                      boxShadow: "0 4px 14px rgba(10,14,39,0.35)",
                      "&:hover": { boxShadow: "0 6px 20px rgba(10,14,39,0.45)", bgcolor: BRAND.navyDark },
                    }}>
                    Sign In
                  </Button>
                </Box>
              </>
            ) : (
              <>
                <Box sx={{ bgcolor: BRAND.navy, px: 4, pt: 3, pb: 3.5, position: "relative", overflow: "hidden" }}>
                  <Box sx={{ position: "absolute", top: -40, right: -40, width: 140, height: 140,
                    borderRadius: "50%", border: "1px solid rgba(99,124,219,0.15)" }} />
                  <Box sx={{ position: "absolute", top: -20, right: -20, width: 90, height: 90,
                    borderRadius: "50%", border: "1px solid rgba(99,124,219,0.2)" }} />
                  <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.18em", color: BRAND.accent, mb: 1 }}>
                    ACCOUNT RECOVERY
                  </Typography>
                  <Typography sx={{ fontSize: 26, fontWeight: 700, color: "#fff", lineHeight: 1.3 }}>
                    Reset Password
                  </Typography>
                  <Typography sx={{ fontSize: 15, color: "rgba(255,255,255,0.55)", mt: 0.5 }}>
                    Choose a new password for your account
                  </Typography>
                </Box>

                <Box sx={{ px: 4, py: 4 }} component="form" onSubmit={handleSubmit}>
                  <AnimatePresence mode="wait">
                    {message && (
                      <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.25 }}>
                        <Alert severity={message.severity} onClose={() => setMessage(null)}
                          sx={{ mb: 2.5, borderRadius: 2, fontSize: 13 }}>
                          {message.text}
                        </Alert>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {!token && (
                    <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2, fontSize: 13 }}>
                      Invalid reset link. Please request a new one from the{" "}
                      <Link to="/forgot-password" style={{ color: "inherit" }}>forgot password</Link> page.
                    </Alert>
                  )}

                  <Field label="New password" required isDark={isDark} hint="Min. 8 characters">
                    <TextField hiddenLabel fullWidth required
                      type={showPw ? "text" : "password"} placeholder="••••••••"
                      value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                      sx={inputSx} slotProps={{ input: { endAdornment: pwAdornment } }} />
                  </Field>

                  <Field label="Confirm new password" required isDark={isDark}>
                    <TextField hiddenLabel fullWidth required
                      type={showPw ? "text" : "password"} placeholder="••••••••"
                      value={confirm} onChange={(e) => setConfirm(e.target.value)}
                      sx={inputSx} />
                  </Field>

                  <Button type="submit" fullWidth variant="contained" disabled={loading || !token}
                    sx={{
                      mt: 0.5, mb: 2,
                      background: `linear-gradient(135deg, ${BRAND.navy} 0%, #3F6AC0 100%)`,
                      borderRadius: "10px", py: 1.6, fontWeight: 700, textTransform: "none", fontSize: 17,
                      boxShadow: "0 4px 14px rgba(10,14,39,0.35)",
                      "&:hover": { boxShadow: "0 6px 20px rgba(10,14,39,0.45)", bgcolor: BRAND.navyDark },
                    }}>
                    {loading ? <CircularProgress size={24} sx={{ color: "#fff" }} /> : "Set New Password"}
                  </Button>

                  <Typography sx={{ textAlign: "center", fontSize: 15, color: textSub }}>
                    <Box component={Link} to="/"
                      sx={{ color: BRAND.accent, textDecoration: "underline", fontWeight: 600 }}>
                      Back to Sign In
                    </Box>
                  </Typography>
                </Box>
              </>
            )}
          </Box>

          <Typography sx={{ mt: 2, fontSize: 13, color: isDark ? "#334155" : "#9ca3af", textAlign: "center" }}>
            AI analysis does not constitute medical advice. Always consult a licensed provider.
          </Typography>
        </motion.div>
      </Box>

      {/* Footer */}
      <Box sx={{ bgcolor: isDark ? "rgba(2,6,15,0.9)" : BRAND.navyDark, py: 2, textAlign: "center",
        position: "relative", zIndex: 10, borderTop: "1px solid rgba(255,255,255,0.05)" }}>
        <Typography sx={{ fontSize: 10.5, color: "rgba(255,255,255,0.25)", letterSpacing: "0.05em" }}>
          © {new Date().getFullYear()} PROOVE GENOMICS. ALL RIGHTS RESERVED.
        </Typography>
      </Box>
    </Box>
  );
}
